import { useCallback, useRef, useState } from 'react';
import { io, type Socket } from 'socket.io-client';

export type JoinState = 'idle' | 'joining' | 'active' | 'ended' | 'error';

export type MusicDevice = 'driver' | 'follower';
export type MicDevice = 'driver' | 'follower' | 'off';

export interface SessionSettings {
  musicDevice: MusicDevice;
  micDevice: MicDevice;
}

export const DEFAULT_SESSION_SETTINGS: SessionSettings = {
  musicDevice: 'follower',
  micDevice: 'follower',
};

export interface TeleprompterSection {
  title: string | null;
  content: string;
  position: number;
}

export interface AudioSectionData {
  position: number;
  audioUrl: string | null;
  audioDurationMs: number | null;
  generationStatus: string;
}

export interface JoinResponse {
  token: string;
  sessionId: string;
  projectId: string;
  project: {
    title: string;
    rawContent: string;
    sections: TeleprompterSection[];
  };
  audioSections: AudioSectionData[];
}

export interface UseJoinSessionReturn {
  state: JoinState;
  error: string | null;
  projectData: JoinResponse | null;
  socket: Socket | null;
  sessionId: string | null;
  sessionSettings: SessionSettings;
  join: (code: string) => Promise<void>;
  reset: () => void;
}

const API_BASE = (typeof import.meta !== 'undefined' && import.meta.env?.VITE_API_URL) || '';
const SYNC_SERVER_URL =
  (typeof import.meta !== 'undefined' && import.meta.env?.VITE_SYNC_SERVER_URL) || '';

export function useJoinSession(): UseJoinSessionReturn {
  const [state, setState] = useState<JoinState>('idle');
  const [error, setError] = useState<string | null>(null);
  const [projectData, setProjectData] = useState<JoinResponse | null>(null);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [sessionSettings, setSessionSettings] = useState<SessionSettings>(DEFAULT_SESSION_SETTINGS);
  const socketRef = useRef<Socket | null>(null);

  const cleanup = useCallback(() => {
    if (socketRef.current) {
      if (socketRef.current.connected) {
        socketRef.current.emit('session:leave');
      }
      socketRef.current.disconnect();
      socketRef.current = null;
    }
  }, []);

  const reset = useCallback(() => {
    cleanup();
    setState('idle');
    setError(null);
    setProjectData(null);
    setSessionId(null);
    setSessionSettings(DEFAULT_SESSION_SETTINGS);
  }, [cleanup]);

  const join = useCallback(
    async (joinCode: string) => {
      setState('joining');
      setError(null);

      try {
        // 1. POST to join endpoint
        const response = await fetch(`${API_BASE}/api/sessions/join`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ joinCode }),
        });

        if (!response.ok) {
          const body = await response.json().catch(() => ({}));
          if (response.status === 429) {
            throw new Error('Too many attempts. Please wait a moment and try again.');
          }
          throw new Error(body.error || 'Failed to join session');
        }

        const data: JoinResponse = await response.json();
        setProjectData(data);
        setSessionId(data.sessionId);

        // 2. Connect socket with guest JWT
        const socket = io(SYNC_SERVER_URL, {
          auth: { token: data.token },
          transports: ['websocket'],
        });
        socketRef.current = socket;

        // 3. Wait for connection and join
        await new Promise<void>((resolve, reject) => {
          const timeout = setTimeout(() => {
            reject(new Error('Connection timed out'));
          }, 10000);

          socket.on('connect', () => {
            socket.emit('session:join', { joinCode });
          });

          socket.on('session:joined', () => {
            clearTimeout(timeout);
            resolve();
          });

          socket.on('session:error', (errData: { message: string }) => {
            clearTimeout(timeout);
            reject(new Error(errData.message));
          });

          socket.on('connect_error', (err: Error) => {
            clearTimeout(timeout);
            reject(err);
          });

          if (socket.connected) {
            socket.emit('session:join', { joinCode });
          }
        });

        // 4. Listen for session events
        socket.on('session:settings', (settings: SessionSettings) => {
          setSessionSettings(settings);
        });

        socket.on('session:ended', () => {
          setState('ended');
        });

        socket.on('session:driver_disconnected', () => {
          setState('ended');
        });

        socket.on('disconnect', () => {
          setState((prev) => (prev === 'active' ? 'ended' : prev));
        });

        setState('active');
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to join session');
        setState('error');
        cleanup();
      }
    },
    [cleanup],
  );

  return {
    state,
    error,
    projectData,
    socket: socketRef.current,
    sessionId,
    sessionSettings,
    join,
    reset,
  };
}
